/**
 * PATH: packages/auth/src/session-manager/authV2CookieRefreshDead.ts
 * FILE_ID: AUTH-SESSION-AUTHV2-COOKIE-REFRESH-DEAD-001
 * PURPOSE: Durable "Auth v2 HttpOnly refresh is dead" latch so cookie-path
 *          refreshers stop re-POSTing `/api/auth/v2/refresh/` after a
 *          definitive 401/403.
 *
 * WHY: BUG-2026-08-03-AUTHV2-COOKIE-REFRESH-401-STORM (sibling of
 *      BUG-2026-06-29-REFRESH-401-LOOP). The v1 dead-token registry keys on a
 *      JS-readable refresh JWT. Under Auth v2 the refresh credential is
 *      HttpOnly (`quotech_refresh`, path=/api/auth/v2/), so JS cannot hash it
 *      and the token tombstone never fires. SessionManager's near-expiry tick,
 *      axiosSetup's 401 interceptor, quotechApi's silent refresh, and the
 *      `@quotech/ui` silentRefresh twin each keep POSTing independently —
 *      operator console on dashboard.quotech.ai storms
 *      `/api/auth/v2/refresh/ 401` while queries still succeed on a still-valid
 *      access JWT.
 *
 * INVARIANT: after the first definitive 401/403 from the Auth v2 cookie
 *            refresh endpoint in this browser, no refresher POSTs again until
 *            a successful login or a successful refresh clears the latch.
 *
 * NOTES FOR FUTURE AI:
 *   - Key string is duplicated in packages/ui (silentRefresh twin) and
 *     packages/tenant-shared/quotechApi — pin with source lockstep tests.
 *   - Do NOT cache a negative in memory only; localStorage survives reload
 *     the way the v1 dead-token registry does.
 */

/** localStorage flag: Auth v2 cookie refresh was definitively rejected. */
export const AUTHV2_COOKIE_REFRESH_DEAD_KEY = 'quotech_authv2_cookie_refresh_dead'

/** Value written when latched (timestamp string for diagnostics). */
function writeDead(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(AUTHV2_COOKIE_REFRESH_DEAD_KEY, String(Date.now()))
  } catch {
    // private-mode / quota — best effort
  }
}

/**
 * Record that POST /api/auth/v2/refresh/ answered 401/403 for this browser.
 * Idempotent.
 */
export function markAuthV2CookieRefreshDead(): void {
  writeDead()
}

/** True when a prior definitive Auth v2 cookie-refresh rejection is latched. */
export function isAuthV2CookieRefreshDead(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(AUTHV2_COOKIE_REFRESH_DEAD_KEY) != null
  } catch {
    return false
  }
}

/**
 * Clear the latch after a successful login or successful Auth v2 refresh.
 * Required so a fresh session is not blocked by a prior tab's dead verdict.
 */
export function clearAuthV2CookieRefreshDead(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(AUTHV2_COOKIE_REFRESH_DEAD_KEY)
  } catch {
    // ignore
  }
}
